import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  RefreshControl,
  Image,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { AuthContext } from "../Context/AuthContext";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { useFocusEffect } from "@react-navigation/native";
import ScorePopup from "../components/ScorePopup";
import GlobalStyles from "../components/StylenColor";
import CircularProgress from "../components/CircularProgress";
import QuarterBadge from "../components/QuarterBadge";
import useTopicCounts from "../components/TopicCountScreen";
import NFProgress from "../components/NFProgress";
import { NetworkContext } from "../Context/NetworkContext";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";

// Desktop web imports
import {
  DashboardLayout,
  PillarCard,
  TopicCard,
  StatCard,
  DesktopTheme,
  useDesktopDetect,
  getPillarColor,
} from "../components/web";

// Quarter selector component for desktop
const QuarterSelector = ({ selectedQuarter, onSelect, quarters }) => {
  const [hoveredQuarter, setHoveredQuarter] = useState(null);

  return (
    <View style={desktopStyles.quarterSelector}>
      {quarters.map((qtr) => (
        <TouchableOpacity
          key={qtr}
          style={[
            desktopStyles.quarterButton,
            selectedQuarter === qtr && desktopStyles.quarterButtonActive,
            hoveredQuarter === qtr && desktopStyles.quarterButtonHover,
          ]}
          onPress={() => onSelect(qtr)}
          onMouseEnter={
            Platform.OS === "web" ? () => setHoveredQuarter(qtr) : undefined
          }
          onMouseLeave={
            Platform.OS === "web" ? () => setHoveredQuarter(null) : undefined
          }
        >
          <Text
            style={[
              desktopStyles.quarterText,
              selectedQuarter === qtr && desktopStyles.quarterTextActive,
            ]}
          >
            {qtr}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
};

// Desktop Dashboard View
const DesktopDashboard = ({
  pillarData,
  quarterAverages,
  availableCount,
  counts,
  missingTopics,
  selectedQuarter,
  setSelectedQuarter,
  visiblePillars,
  setVisiblePillars,
  quarterLoading,
  setQuarterLoading,
  loading,
  handleLongPress,
  navigation,
}) => {
  const pillars = ["Environmental", "Social", "Governance"];
  const quarters = ["Q1", "Q2", "Q3", "Q4"];

  // Calculate overall ESG score
  const overallScore =
    pillars.reduce((sum, pillar) => {
      return sum + (quarterAverages[pillar]?.[selectedQuarter] || 0);
    }, 0) / 3;

  // Get previous quarter score
  const prevQuarterMap = { Q1: null, Q2: "Q1", Q3: "Q2", Q4: "Q3" };
  const prevQuarter = prevQuarterMap[selectedQuarter];
  const prevOverallScore = prevQuarter
    ? pillars.reduce((sum, pillar) => {
        return sum + (quarterAverages[pillar]?.[prevQuarter] || 0);
      }, 0) / 3
    : null;

  return (
    <DashboardLayout
      currentRoute="ESG Pillars"
      quarterSelector={
        <QuarterSelector
          selectedQuarter={selectedQuarter}
          onSelect={(qtr) => {
            setQuarterLoading(true);
            setSelectedQuarter(qtr);
          }}
          quarters={quarters}
        />
      }
    >
      <ScrollView
        style={desktopStyles.content}
        contentContainerStyle={desktopStyles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Overall Score Hero */}
        <View style={desktopStyles.heroSection}>
          <StatCard
            title="Overall ESG Score"
            value={Math.round(overallScore)}
            suffix="%"
            previousValue={prevOverallScore ? Math.round(prevOverallScore) : null}
            icon="🎯"
            color={DesktopTheme.colors.primary}
            style={desktopStyles.heroCard}
          />
          <View style={desktopStyles.heroInfo}>
            <Text style={desktopStyles.heroTitle}>ESG Performance Overview</Text>
            <Text style={desktopStyles.heroSubtitle}>
              {selectedQuarter} {new Date().getFullYear()} Assessment
            </Text>
            <Text style={desktopStyles.heroDescription}>
              Track your organization's Environmental, Social, and Governance
              performance across all departments.
            </Text>
            <TouchableOpacity
              style={desktopStyles.contributeButton}
              onPress={() => navigation.navigate("EmployeeLoginScreen")}
            >
              <Text style={desktopStyles.contributeButtonText}>
                + Contribute Assessment
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Pillar Cards Grid */}
        <View style={desktopStyles.sectionHeader}>
          <Text style={desktopStyles.sectionTitle}>Pillar Scores</Text>
          <Text style={desktopStyles.sectionSubtitle}>
            Click on a pillar to view topic breakdown
          </Text>
        </View>

        <View style={desktopStyles.pillarGrid}>
          {pillars.map((pillar) => {
            const score = quarterAverages[pillar]?.[selectedQuarter] || 0;
            const prevScore = prevQuarter
              ? quarterAverages[pillar]?.[prevQuarter]
              : null;
            const topicsCompleted = availableCount[pillar]?.length || 0;
            const totalTopics = counts[pillar] || 0;

            return (
              <PillarCard
                key={pillar}
                pillar={pillar}
                score={score}
                previousScore={prevScore}
                topicsCompleted={topicsCompleted}
                totalTopics={totalTopics}
                isExpanded={visiblePillars[pillar]}
                onPress={() =>
                  setVisiblePillars((prev) => ({
                    ...prev,
                    [pillar]: !prev[pillar],
                  }))
                }
                onExpandPress={() =>
                  setVisiblePillars((prev) => ({
                    ...prev,
                    [pillar]: !prev[pillar],
                  }))
                }
                style={desktopStyles.pillarCard}
              />
            );
          })}
        </View>

        {/* Expanded Topic Lists */}
        {pillars.map((pillar) => {
          if (!visiblePillars[pillar]) return null;

          const pillarColor = getPillarColor(pillar);
          const sortedTopics = [...(pillarData[pillar] || [])].sort((a, b) => {
            const scoreA = a.outerValue ?? 0;
            const scoreB = b.outerValue ?? 0;
            return scoreB - scoreA;
          });
          const missing = missingTopics[pillar] || [];

          return (
            <View key={`topics-${pillar}`} style={desktopStyles.topicSection}>
              <View style={desktopStyles.topicSectionHeader}>
                <View
                  style={[
                    desktopStyles.topicSectionIndicator,
                    { backgroundColor: pillarColor },
                  ]}
                />
                <Text style={desktopStyles.topicSectionTitle}>
                  {pillar} Topics
                </Text>
                <Text style={desktopStyles.topicSectionCount}>
                  {sortedTopics.filter((t) => t.outerValue !== null).length} of{" "}
                  {sortedTopics.length + missing.length} completed
                </Text>
              </View>

              {quarterLoading ? (
                <ActivityIndicator
                  size="large"
                  color={pillarColor}
                  style={{ marginVertical: 40 }}
                />
              ) : (
                <View style={desktopStyles.topicGrid}>
                  {sortedTopics.map((item, index) => (
                    <TopicCard
                      key={`${pillar}-topic-${index}`}
                      topic={item.topic}
                      score={item.outerValue}
                      previousScore={item.innerValue}
                      pillar={pillar}
                      date={item.currDate}
                      onLongPress={() =>
                        handleLongPress(
                          item.topic,
                          item.innerValue,
                          item.outerValue,
                          item.prevDate,
                          item.currDate
                        )
                      }
                      onPress={() =>
                        handleLongPress(
                          item.topic,
                          item.innerValue,
                          item.outerValue,
                          item.prevDate,
                          item.currDate
                        )
                      }
                      isNotFilled={item.outerValue === null}
                      style={desktopStyles.topicCard}
                    />
                  ))}
                  {missing.sort().map((topic, index) => (
                    <TopicCard
                      key={`${pillar}-missing-${index}`}
                      topic={topic}
                      pillar={pillar}
                      isNotFilled={true}
                      onPress={() =>
                        handleLongPress(
                          topic,
                          "N/A",
                          "N/A",
                          "N/A",
                          "NOT FILLED for this quarter"
                        )
                      }
                      style={desktopStyles.topicCard}
                    />
                  ))}
                </View>
              )}
            </View>
          );
        })}

        {/* Bottom padding for scroll */}
        <View style={{ height: 40 }} />
      </ScrollView>
    </DashboardLayout>
  );
};

// Main Pillar Screen
export default function Pillar() {
  const { width } = useWindowDimensions();
  const { isDesktopWeb } = useDesktopDetect();
  const navigation = useNavigation();
  const { companyId } = useContext(AuthContext);
  const [pillarData, setPillarData] = useState({
    Environmental: [],
    Social: [],
    Governance: [],
  });
  const getCurrentQuarter = () => {
    const month = new Date().getMonth() + 1;
    if (month >= 1 && month <= 3) return "Q1";
    if (month >= 4 && month <= 6) return "Q2";
    if (month >= 7 && month <= 9) return "Q3";
    return "Q4";
  };
  const [refreshing, setRefreshing] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedPrevScore, setSelectedPrevScore] = useState(0);
  const [selectedCurrScore, setSelectedCurrScore] = useState(0);
  const [selectedPrevDate, setSelectedPrevDate] = useState("");
  const [selectedCurrDate, setSelectedCurrDate] = useState("");
  const [quarterLoading, setQuarterLoading] = useState(false);
  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarter());
  const [visiblePillars, setVisiblePillars] = useState({
    Environmental: false,
    Social: false,
    Governance: false,
  });
  const [availableCount, setAvailableCount] = useState({
    Environmental: [],
    Social: [],
    Governance: [],
  });
  const [quarterAverages, setQuarterAverages] = useState({
    Environmental: {},
    Social: {},
    Governance: {},
  });
  const { counts, loading, topics } = useTopicCounts();
  const [missingTopics, setMissingTopics] = useState({
    Environmental: [],
    Social: [],
    Governance: [],
  });
  const { isConnected } = useContext(NetworkContext);

  const togglePillarVisibility = (pillar) => {
    setVisiblePillars((prev) => ({
      ...prev,
      [pillar]: !prev[pillar],
    }));
  };

  const getPreviousQuarter = (quarter) => {
    const quarters = ["Q1", "Q2", "Q3", "Q4"];
    const index = quarters.indexOf(quarter);
    return index > 0 ? quarters[index - 1] : null;
  };

  const fetchResponses = async () => {
    try {
      const responsesRef = collection(
        db,
        `companies/${companyId}/questionnaireResponses`
      );
      const q = query(responsesRef, orderBy("timestamp", "desc"));
      const snapshot = await getDocs(q);

      const quarterlyScores = {
        Environmental: { Q1: [], Q2: [], Q3: [], Q4: [] },
        Social: { Q1: [], Q2: [], Q3: [], Q4: [] },
        Governance: { Q1: [], Q2: [], Q3: [], Q4: [] },
      };

      let groupedTopics = {};

      const currentQuarter = getCurrentQuarter();
      const currentQuarterTopics = {
        Environmental: new Set(),
        Social: new Set(),
        Governance: new Set(),
      };

      snapshot.forEach((doc) => {
        const data = doc.data();
        const {
          topic,
          totalScore,
          pillar,
          NumberOfQuestions,
          timestamp,
          quarter,
        } = data;

        const maxScore = (NumberOfQuestions || 5) * 5;
        const percentage = (totalScore / maxScore) * 100;

        if (pillar && quarterlyScores[pillar] && quarter) {
          quarterlyScores[pillar][quarter].push(percentage);
        }

        if (!groupedTopics[topic]) {
          groupedTopics[topic] = { scores: [], dates: [], pillar, maxScore };
        }

        groupedTopics[topic].scores.push(totalScore);
        groupedTopics[topic].dates.push(
          new Date(timestamp?.toDate()).toLocaleDateString()
        );

        if (quarter === currentQuarter && pillar) {
          currentQuarterTopics[pillar].add(topic);
        }
      });

      const averageQuarterlyScores = {};
      Object.entries(quarterlyScores).forEach(([pillar, quarters]) => {
        averageQuarterlyScores[pillar] = {};
        Object.entries(quarters).forEach(([qtr, values]) => {
          const avg =
            values.length > 0
              ? values.reduce((a, b) => a + b, 0) / values.length
              : 0;
          averageQuarterlyScores[pillar][qtr] = Math.round(avg);
        });
      });

      let structuredData = { Environmental: [], Social: [], Governance: [] };
      Object.entries(groupedTopics).forEach(
        ([topic, { scores, dates, pillar, maxScore }]) => {
          const quarterScores = snapshot.docs
            .filter(
              (doc) =>
                doc.data().topic === topic && doc.data().pillar === pillar
            )
            .map((doc) => {
              const data = doc.data();
              return {
                quarter: data.quarter,
                score: data.totalScore,
                date: new Date(data.timestamp?.toDate()).toLocaleDateString(),
                maxScore: data.NumberOfQuestions * 5 || 25,
              };
            });

          const getScoreByQuarter = (qtr) => {
            const entry = quarterScores.find((e) => e.quarter === qtr);
            return entry
              ? {
                  value: (entry.score / entry.maxScore) * 100,
                  date: entry.date,
                }
              : { value: null, date: "N/A" };
          };

          const current = getScoreByQuarter(selectedQuarter);
          const prevQuarterMap = { Q1: "Q0", Q2: "Q1", Q3: "Q2", Q4: "Q3" };
          const prev = getScoreByQuarter(
            prevQuarterMap[selectedQuarter] || "Q0"
          );

          structuredData[pillar].push({
            topic,
            outerValue: current.value,
            innerValue: prev.value,
            currDate: current.date,
            prevDate: prev.date,
          });
        }
      );

      const filteredData = {
        Environmental: structuredData.Environmental.filter(
          (item) => item.outerValue !== null
        ),
        Social: structuredData.Social.filter(
          (item) => item.outerValue !== null
        ),
        Governance: structuredData.Governance.filter(
          (item) => item.outerValue !== null
        ),
      };

      setPillarData(structuredData);
      setAvailableCount(filteredData);
      setQuarterAverages(averageQuarterlyScores);

      const missingTopicsData = {
        Environmental: [],
        Social: [],
        Governance: [],
      };
      ["Environmental", "Social", "Governance"].forEach((pillar) => {
        const expectedTopics = topics[pillar] || [];
        const answeredTopics = Array.from(currentQuarterTopics[pillar]);
        const answeredSet = new Set(answeredTopics);

        expectedTopics.forEach((topic) => {
          if (!answeredSet.has(topic)) {
            missingTopicsData[pillar].push(topic);
          }
        });
      });
      setMissingTopics(missingTopicsData);
    } catch (error) {
      console.error("Error fetching responses:", error);
    }
  };

  const handleLongPress = (topic, prevScore, currScore, prevDate, currDate) => {
    setSelectedTopic(topic);
    setSelectedPrevScore(prevScore);
    setSelectedCurrScore(currScore);
    setSelectedPrevDate(prevDate);
    setSelectedCurrDate(currDate);
    setPopupVisible(true);
  };

  useEffect(() => {
    if (!loading && companyId) {
      fetchResponses().finally(() => setQuarterLoading(false));
    }
    if (!isConnected) {
      if (Platform.OS === "web") {
        window.alert("You are currently offline. Some features may not work.");
      } else {
        alert("You are currently offline. Some features may not work.");
      }
    }
  }, [loading, companyId, isConnected, selectedQuarter]);

  useFocusEffect(
    useCallback(() => {
      if (!loading && companyId) {
        fetchResponses();
      }
    }, [loading, companyId])
  );

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    if (!loading) {
      fetchResponses().then(() => setRefreshing(false));
    } else {
      setRefreshing(false);
    }
  }, [loading]);

  const pillarImages = {
    Environmental: require("../images/Enviro2.png"),
    Social: require("../images/Soc.png"),
    Governance: require("../images/Gover.png"),
  };

  // Render desktop version
  if (isDesktopWeb) {
    return (
      <>
        <DesktopDashboard
          pillarData={pillarData}
          quarterAverages={quarterAverages}
          availableCount={availableCount}
          counts={counts}
          missingTopics={missingTopics}
          selectedQuarter={selectedQuarter}
          setSelectedQuarter={setSelectedQuarter}
          visiblePillars={visiblePillars}
          setVisiblePillars={setVisiblePillars}
          quarterLoading={quarterLoading}
          setQuarterLoading={setQuarterLoading}
          loading={loading}
          handleLongPress={handleLongPress}
          navigation={navigation}
        />
        <ScorePopup
          visible={popupVisible}
          onClose={() => setPopupVisible(false)}
          topic={selectedTopic}
          prevScore={
            selectedPrevScore > 0 ? Math.round(selectedPrevScore) : null
          }
          currScore={Math.round(selectedCurrScore)}
          prevDate={selectedPrevDate}
          currDate={selectedCurrDate}
          selectedQuarter={selectedQuarter}
        />
      </>
    );
  }

  // Original mobile version
  return (
    <View style={styles.screenContainer}>
      <ScrollView
        style={styles.container}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <View
          style={[
            Platform.OS === "web" &&
              width > 900 && {
                flexDirection: "row",
                justifyContent: "space-between",
                width: "100%",
                gap: 20,
              },
          ]}
        >
          {["Environmental", "Social", "Governance"].map((pillar) => (
            <View
              key={pillar}
              style={[
                Platform.OS === "web" &&
                  width > 900 && {
                    flexDirection: "column",
                    alignItems: "center",
                    width: "32.5%",
                  },
              ]}
            >
              <View
                style={[
                  styles.headerContainer,
                  Platform.OS === "web" &&
                    width > 900 &&
                    mobileDesktopStyles.desktopHeaderContainer,
                ]}
              >
                <Image
                  source={pillarImages[pillar]}
                  style={[
                    styles.iconImage,
                    Platform.OS === "web" &&
                      width > 900 &&
                      mobileDesktopStyles.desktopIconImage,
                  ]}
                  resizeMode="contain"
                />
                <View
                  style={[
                    styles.headerCenterContainer,
                    Platform.OS === "web" &&
                      width > 900 &&
                      mobileDesktopStyles.desktopHeaderCenterContainer,
                  ]}
                >
                  <Text
                    style={[
                      styles.headerText,
                      GlobalStyles.btntxtoffset,
                      Platform.OS === "web" &&
                        width > 900 && { fontSize: GlobalStyles.fontSize.md },
                    ]}
                  >
                    {pillar}
                  </Text>
                </View>
                <View
                  style={[
                    styles.statsContainer,
                    Platform.OS === "web" &&
                      width > 900 &&
                      mobileDesktopStyles.desktopStatsContainer,
                  ]}
                >
                  <Text
                    style={[
                      styles.countText,
                      GlobalStyles.btntxtoffset,
                      Platform.OS === "web" &&
                        width > 900 &&
                        mobileDesktopStyles.desktopStatsText,
                    ]}
                  >
                    {loading
                      ? "Load..."
                      : `${availableCount[pillar]?.length || 0} / ${
                          counts[pillar] || 0
                        }`}
                  </Text>
                  <Text
                    style={[
                      styles.percentageText,
                      GlobalStyles.btntxtoffset,
                      Platform.OS === "web" &&
                        width > 900 &&
                        mobileDesktopStyles.desktopStatsText,
                    ]}
                  >
                    {quarterAverages[pillar]?.[selectedQuarter] ?? 0}%
                  </Text>
                </View>
              </View>

              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-around",
                  marginVertical: 10,
                  alignSelf: "center",
                }}
              >
                {Object.entries(quarterAverages[pillar] || {}).map(
                  ([qtr, avg], index) => (
                    <TouchableOpacity
                      key={index}
                      onPress={() => {
                        if (selectedQuarter === qtr) {
                          setVisiblePillars((prev) => ({
                            ...prev,
                            [pillar]: !prev[pillar],
                          }));
                        } else {
                          setQuarterLoading(true);
                          setSelectedQuarter(qtr);
                          setVisiblePillars((prev) => ({
                            ...prev,
                            [pillar]: true,
                          }));
                        }
                      }}
                    >
                      <QuarterBadge
                        quarter={qtr}
                        percentage={avg === 0 ? null : avg}
                        selected={selectedQuarter === qtr}
                      />
                    </TouchableOpacity>
                  )
                )}
              </View>

              {visiblePillars[pillar] &&
                (quarterLoading ? (
                  <ActivityIndicator
                    size="large"
                    color="#007B55"
                    style={{ marginVertical: 20 }}
                  />
                ) : (
                  <View
                    style={[
                      styles.gridContainer,
                      Platform.OS === "web" &&
                        width > 900 && {
                          justifyContent: "flex-start",
                          gap: 0,
                          rowGap: 0,
                          width: "100%",
                        },
                    ]}
                  >
                    {pillarData[pillar]
                      .sort((a, b) => {
                        const scoreA = a.outerValue ?? 0;
                        const scoreB = b.outerValue ?? 0;
                        return scoreB - scoreA;
                      })
                      .map((item, index) => (
                        <CircularProgress
                          key={`filled-${index}`}
                          outerValue={item.outerValue}
                          innerValue={item.innerValue}
                          topic={item.topic}
                          onLongPress={() =>
                            handleLongPress(
                              item.topic,
                              item.innerValue,
                              item.outerValue,
                              item.prevDate,
                              item.currDate
                            )
                          }
                        />
                      ))}
                    {missingTopics[pillar].sort().map((topic, index) => (
                      <NFProgress
                        key={`missing-${index}`}
                        topic={topic}
                        onLongPress={() => {
                          setSelectedTopic(topic);
                          setSelectedPrevScore("N/A");
                          setSelectedCurrScore("N/A");
                          setSelectedPrevDate("N/A");
                          setSelectedCurrDate("NOT FILLED for this quarter");
                          setPopupVisible(true);
                        }}
                      />
                    ))}
                  </View>
                ))}
            </View>
          ))}
        </View>
      </ScrollView>

      <TouchableOpacity
        style={[
          styles.floatingButton,
          GlobalStyles.btnBgShadow,
          Platform.OS === "web" &&
            width > 900 && {
              position: "fixed",
              right: 30,
              bottom: 30,
              width: 260,
              height: 60,
              paddingHorizontal: 20,
              borderRadius: 30,
              zIndex: 999,
            },
        ]}
        onPress={() => navigation.navigate("EmployeeLoginScreen")}
      >
        <Text
          style={[
            styles.buttonText,
            GlobalStyles.btntxtoffset,
            Platform.OS === "web" &&
              width > 900 && {
                fontSize: 18,
                textAlign: "center",
              },
          ]}
        >
          Contribute to your department
        </Text>
      </TouchableOpacity>

      <ScorePopup
        visible={popupVisible}
        onClose={() => setPopupVisible(false)}
        topic={selectedTopic}
        prevScore={selectedPrevScore > 0 ? Math.round(selectedPrevScore) : null}
        currScore={Math.round(selectedCurrScore)}
        prevDate={selectedPrevDate}
        currDate={selectedCurrDate}
        selectedQuarter={selectedQuarter}
      />
    </View>
  );
}

// Mobile styles (original)
const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
    alignContent: "center",
    alignItems: "center",
    backgroundColor: GlobalStyles.GlobalBg.backgroundColor,
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: GlobalStyles.GlobalBg.headerColor,
    paddingHorizontal: 15,
    paddingVertical: 10,
    marginBottom: 10,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: GlobalStyles.GlobalBg.headerBorder,
    height: hp("10.8%"),
  },
  headerCenterContainer: {
    position: "absolute",
    left: 0,
    right: 0,
    alignItems: "center",
  },
  statsContainer: {
    alignItems: "center",
    justifyContent: "center",
    alignContent: "center",
  },
  headerText: {
    fontSize: GlobalStyles.fontSize.xl,
    color: "white",
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    textAlign: "center",
    flex: 1,
    paddingVertical: 1,
    paddingHorizontal: 5,
  },
  percentageText: {
    color: "white",
    fontSize: GlobalStyles.fontSize.lg,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  iconImage: {
    width: wp("12%"),
  },
  container: {
    flex: 1,
    width: "100%",
    backgroundColor: GlobalStyles.GlobalBg.backgroundColor,
    paddingTop: 10,
    paddingHorizontal: 15,
    paddingBottom: 20,
    marginBottom: 0,
  },
  gridContainer: {
    marginTop: 10,
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    paddingHorizontal: 5,
    marginBottom: hp("10%"),
  },
  floatingButton: {
    position: "absolute",
    bottom: 20,
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    borderRadius: 50,
    width: wp("90%"),
    height: wp("16%"),
    alignItems: "center",
    justifyContent: "center",
    alignContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 5,
    paddingHorizontal: 10,
  },
  buttonText: {
    color: "white",
    fontSize: GlobalStyles.fontSize.md,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    paddingVertical: 1,
  },
  countText: {
    color: "white",
    fontSize: GlobalStyles.fontSize.lg,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginBottom: 5,
  },
});

// Mobile desktop styles (for non-new-layout fallback)
const mobileDesktopStyles = StyleSheet.create({
  desktopHeaderContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    backgroundColor: GlobalStyles.GlobalBg.headerColor,
    paddingHorizontal: 5,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: GlobalStyles.GlobalBg.headerBorder,
    height: "auto",
  },
  desktopIconImage: {
    width: 40,
    height: 40,
    resizeMode: "contain",
    marginLeft: 5,
  },
  desktopHeaderCenterContainer: {
    flex: 1,
    alignItems: "center",
  },
  desktopStatsContainer: {
    alignItems: "flex-end",
    justifyContent: "center",
    alignContent: "center",
  },
  desktopStatsText: {
    fontSize: GlobalStyles.fontSize.sm,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginRight: 5,
  },
});

// Desktop styles (new layout)
const desktopStyles = StyleSheet.create({
  content: {
    flex: 1,
    backgroundColor: DesktopTheme.colors.contentBg,
  },
  contentContainer: {
    padding: DesktopTheme.spacing.xl,
    maxWidth: DesktopTheme.layout.maxContentWidth,
  },

  // Hero Section
  heroSection: {
    flexDirection: "row",
    marginBottom: DesktopTheme.spacing.xl,
    gap: DesktopTheme.spacing.xl,
  },
  heroCard: {
    minWidth: 280,
  },
  heroInfo: {
    flex: 1,
    justifyContent: "center",
    paddingLeft: DesktopTheme.spacing.lg,
  },
  heroTitle: {
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontSize: DesktopTheme.typography.fontSize.h2,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: DesktopTheme.spacing.xs,
  },
  heroSubtitle: {
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontSize: DesktopTheme.typography.fontSize.body,
    color: DesktopTheme.colors.textSecondary,
    marginBottom: DesktopTheme.spacing.md,
  },
  heroDescription: {
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontSize: DesktopTheme.typography.fontSize.body,
    color: DesktopTheme.colors.textMuted,
    lineHeight: 22,
    marginBottom: DesktopTheme.spacing.lg,
    maxWidth: 500,
  },
  contributeButton: {
    backgroundColor: DesktopTheme.colors.primary,
    paddingVertical: DesktopTheme.spacing.md,
    paddingHorizontal: DesktopTheme.spacing.xl,
    borderRadius: DesktopTheme.borderRadius.md,
    alignSelf: "flex-start",
  },
  contributeButtonText: {
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontSize: DesktopTheme.typography.fontSize.body,
    color: "#fff",
  },

  // Section Header
  sectionHeader: {
    marginBottom: DesktopTheme.spacing.lg,
  },
  sectionTitle: {
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontSize: DesktopTheme.typography.fontSize.h3,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: DesktopTheme.spacing.xs,
  },
  sectionSubtitle: {
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontSize: DesktopTheme.typography.fontSize.body,
    color: DesktopTheme.colors.textMuted,
  },

  // Pillar Grid
  pillarGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: DesktopTheme.spacing.lg,
    marginBottom: DesktopTheme.spacing.xl,
  },
  pillarCard: {
    flex: 1,
    minWidth: 280,
    maxWidth: 400,
  },

  // Topic Section
  topicSection: {
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: DesktopTheme.spacing.lg,
    marginBottom: DesktopTheme.spacing.lg,
    ...DesktopTheme.shadows.card,
  },
  topicSectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: DesktopTheme.spacing.lg,
  },
  topicSectionIndicator: {
    width: 4,
    height: 24,
    borderRadius: 2,
    marginRight: DesktopTheme.spacing.md,
  },
  topicSectionTitle: {
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontSize: DesktopTheme.typography.fontSize.h4,
    color: DesktopTheme.colors.textPrimary,
    flex: 1,
  },
  topicSectionCount: {
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontSize: DesktopTheme.typography.fontSize.small,
    color: DesktopTheme.colors.textMuted,
  },
  topicGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: DesktopTheme.spacing.md,
  },
  topicCard: {
    width: "calc(50% - 8px)",
    minWidth: 300,
  },

  // Quarter Selector
  quarterSelector: {
    flexDirection: "row",
    backgroundColor: DesktopTheme.colors.contentBg,
    borderRadius: DesktopTheme.borderRadius.md,
    padding: 4,
  },
  quarterButton: {
    paddingVertical: DesktopTheme.spacing.sm,
    paddingHorizontal: DesktopTheme.spacing.md,
    borderRadius: DesktopTheme.borderRadius.sm,
  },
  quarterButtonActive: {
    backgroundColor: DesktopTheme.colors.primary,
  },
  quarterButtonHover: {
    backgroundColor: DesktopTheme.colors.border,
  },
  quarterText: {
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontSize: DesktopTheme.typography.fontSize.small,
    color: DesktopTheme.colors.textSecondary,
  },
  quarterTextActive: {
    color: "#fff",
  },
});
